import { ReactElement } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Nav } from '@betterment/samba-react';
import { useFeatureFlag } from '@betterment/shared-components';

import { AccountItem } from './AccountItem';
import { EnvelopeItem } from './EnvelopeItem';
import { SidebarAccountsDocument, SidebarAccountsQuery } from './graphql/query.sidebarAccounts.generated';
import { HomeLink } from './HomeLink';

import { addAccountUrl } from '../../../Shared/addAccountUrl';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { ListShimmer } from '../ListShimmer';
import { NavLink } from '../nav-link/NavLink';

type ProductOnboardingOption = SidebarAccountsQuery['productOnboardingOptions'][number];

export const AccountsNav = () => {
  const { enabled: isEnvelopesReorderingEnabled } = useFeatureFlag({
    splitName: 'retail.envelopes_reordering_q4_2025_enabled',
    context: 'EnvelopesNavLinks',
  });

  const { enabled: isSolo401kImprovementsEnabled } = useFeatureFlag({
    splitName: 'retail.solo_401k_improvements_q1_2026_enabled',
    context: 'NavLinks',
  });
  const { data } = useGraphQLQuery(SidebarAccountsDocument, {
    fetchPolicy: 'cache-first',
  });

  if (!data) return <ListShimmer />;

  const { productOnboardingOptions, envelopes } = data;

  const productOnboardingOptionLinks = productOnboardingOptions.reduce<ReactElement[]>(
    (links, option: ProductOnboardingOption) => {
      if (option.__typename === 'PremiumOnboardingOption') {
        return links;
      }

      const addAccountUrlParams: Record<string, string> = { product_category: option.legacyProductCategory };
      if (option.__typename === 'Solo401kOnboardingOption' && isSolo401kImprovementsEnabled) {
        addAccountUrlParams.qualified = 'true';
      }

      const href =
        option.__typename === 'Solo401kOnboardingOption' && !isSolo401kImprovementsEnabled
          ? urlBuilder.appAdviceInitiationFlow({
              signup_intent: option.legacyProductCategory,
              product_category: option.legacyProductCategory,
            })
          : addAccountUrl(addAccountUrlParams, false);

      if (option.isInProgress && envelopes.length === 0) {
        links.push(
          <Nav.ListItem key={option.id}>
            <NavLink to={href} reloadDocument={true}>
              <NavLink.Icon asChild={true}>
                <img src={option.image.light} alt="" />
              </NavLink.Icon>
              <NavLink.Text>{option.name}</NavLink.Text>
            </NavLink>
          </Nav.ListItem>
        );
      }

      return links;
    },
    []
  );

  const sortedEnvelopes = [...data.envelopes];

  if (isEnvelopesReorderingEnabled) {
    sortedEnvelopes.sort((a, b) => {
      const isAChecking = a.accounts.some(account => account.__typename === 'CheckingAccount');
      const isBChecking = b.accounts.some(account => account.__typename === 'CheckingAccount');

      // Keep checking accounts first
      if (isAChecking && !isBChecking) return -1;
      if (!isAChecking && isBChecking) return 1;

      // If both have checking or neither have checking, sort by orderPosition
      if (a.orderPosition == null && b.orderPosition == null) return 0;
      if (a.orderPosition == null) return 1;
      if (b.orderPosition == null) return -1;
      return a.orderPosition - b.orderPosition;
    });
  }

  const envelopeLinks = sortedEnvelopes.reduce<ReactElement[]>((links, envelope) => {
    if (!envelope.purpose) {
      links.push(
        ...envelope.accounts.map(account => (
          <Nav.ListItem key={account.id}>
            <AccountItem account={account} />
          </Nav.ListItem>
        ))
      );
    } else {
      links.push(
        <Nav.ListItem key={envelope.id}>
          <EnvelopeItem envelope={envelope} />
        </Nav.ListItem>
      );
    }

    return links;
  }, []);

  const links: ReactElement[] = [
    <Nav.ListItem key="home">
      <HomeLink />
    </Nav.ListItem>,
    ...productOnboardingOptionLinks,
    ...envelopeLinks,
  ];

  return (
    <Nav aria-label="Your accounts">
      {links.length === 1 ? <HomeLink /> : <Nav.List>{links.map(link => link)}</Nav.List>}
    </Nav>
  );
};
